import React, { useState, useCallback } from 'react'
import { connect } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@material-ui/icons/Close'
import CircularProgress from '@material-ui/core/CircularProgress'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import { makeStyles } from '@material-ui/core/styles'
import { organizationActions } from '../../_state/actions'

const useStyles = makeStyles((theme) => ({
    dropzone: {
        border: '2px dashed #ccc',
        borderRadius: 8,
        padding: '40px 20px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.2s, background-color 0.2s',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: '#f5f9ff',
        },
    },
    dropzoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#e3f2fd',
    },
    stepSection: {
        marginBottom: 24,
    },
    stepTitle: {
        fontWeight: 600,
        marginBottom: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    resultSummary: {
        backgroundColor: '#f5f5f5',
        borderRadius: 8,
        padding: 16,
        marginTop: 16,
    },
    resultRow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
    },
    errorTable: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: 12,
    },
    errorTableHeader: {
        backgroundColor: '#f5f5f5',
        textAlign: 'left',
        padding: '8px 12px',
        fontWeight: 600,
        fontSize: 13,
    },
    errorTableCell: {
        padding: '6px 12px',
        borderBottom: '1px solid #eee',
        fontSize: 13,
    },
    selectedFile: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 12,
        padding: '8px 12px',
        backgroundColor: '#e8f5e9',
        borderRadius: 4,
    },
}))

const UserImportDialog = ({ open, onClose, organization, dispatch }) => {
    const classes = useStyles()
    const [selectedFile, setSelectedFile] = useState(null)
    const organizationId = organization.selectedOrganization

    const onDrop = useCallback((acceptedFiles) => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0])
        }
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx',
        maxFiles: 1,
    })

    const handleUpload = () => {
        if (selectedFile) {
            dispatch(organizationActions.uploadUserImport(organizationId, selectedFile))
        }
    }

    const handleClose = () => {
        setSelectedFile(null)
        dispatch(organizationActions.resetUserImport())
        onClose()
    }

    const importResult = organization.userImportResult
    const isUploading = organization.isUploadingUserImport

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle disableTypography style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Import Users from Excel</Typography>
                <IconButton size="small" onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <div className={classes.stepSection}>
                    <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                        Upload a filled-in Excel template with user details (email, name, phone, classrooms).
                    </Typography>

                    {!importResult && (
                        <>
                            <div
                                {...getRootProps()}
                                className={`${classes.dropzone} ${isDragActive ? classes.dropzoneActive : ''}`}>
                                <input {...getInputProps()} />
                                <CloudUploadIcon style={{ fontSize: 40, color: '#9e9e9e', marginBottom: 8 }} />
                                <Typography variant="body2" color="textSecondary">
                                    {isDragActive
                                        ? 'Drop the Excel file here...'
                                        : 'Drag and drop an .xlsx file here, or click to select'}
                                </Typography>
                            </div>

                            {selectedFile && (
                                <div className={classes.selectedFile}>
                                    <CheckCircleIcon style={{ color: '#4caf50', fontSize: 20 }} />
                                    <Typography variant="body2">{selectedFile.name}</Typography>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {importResult && (
                    <div className={classes.stepSection}>
                        <Typography className={classes.stepTitle}>
                            <CheckCircleIcon style={{ color: '#4caf50' }} />
                            Import Complete
                        </Typography>

                        <div className={classes.resultSummary}>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">Total rows processed</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {importResult.totalRows}
                                </Typography>
                            </div>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">New users invited</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#4caf50' }}>
                                    {importResult.usersInvited}
                                </Typography>
                            </div>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">Users already in organization</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600 }}>
                                    {importResult.usersAlreadyInOrganization}
                                </Typography>
                            </div>
                            <div className={classes.resultRow}>
                                <Typography variant="body2">Classroom assignments</Typography>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#4caf50' }}>
                                    {importResult.classroomAssignments}
                                </Typography>
                            </div>
                        </div>

                        {importResult.errors && importResult.errors.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#f44336', marginBottom: 8 }}>
                                    <ErrorIcon style={{ fontSize: 16, verticalAlign: 'middle', marginRight: 4 }} />
                                    {importResult.errors.length} row(s) had errors:
                                </Typography>
                                <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                                    <table className={classes.errorTable}>
                                        <thead>
                                            <tr>
                                                <th className={classes.errorTableHeader}>Row</th>
                                                <th className={classes.errorTableHeader}>Email</th>
                                                <th className={classes.errorTableHeader}>Error</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {importResult.errors.map((err, idx) => (
                                                <tr key={idx}>
                                                    <td className={classes.errorTableCell}>{err.rowNumber}</td>
                                                    <td className={classes.errorTableCell}>{err.email}</td>
                                                    <td className={classes.errorTableCell}>{err.errorMessage}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>

            <DialogActions style={{ padding: '12px 24px' }}>
                <Button onClick={handleClose} color="default">
                    {importResult ? 'Close' : 'Cancel'}
                </Button>
                {!importResult && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpload}
                        disabled={!selectedFile || isUploading}
                        startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}>
                        {isUploading ? 'Importing...' : 'Import Users'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

function mapStateToProps(state) {
    const { organization } = state
    return { organization }
}

export default connect(mapStateToProps)(UserImportDialog)
